import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import {
    Agenda,
    AgendaDetail,
    AgendaPreview,
    CreateAgendaDto,
    UpdateAgendaDto,
    AgendaListResponse,
    AgendaStatus,
    ConfirmAgendaDto,
    MoveAgendaCenterRequestDto,
    SmartAssignRequest,
    SmartAssignResponse,
    SmartAssignBulkRequest,
    SmartAssignBulkResponse
} from '../../../models/agenda.model';
import { environment } from '../../../../environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { HttpError } from '@microsoft/signalr';
export type CreateAgendaResponse = { agenda: AgendaPreview; warnings?: string[] } | AgendaPreview;

@Injectable({
    providedIn: 'root'
})
export class AgendaService {
    private readonly apiUrl = `${environment.cafler_backoffice_back_url}/Agenda`;

    constructor(private readonly http: HttpClient, private readonly authService: AuthService, private readonly analytics: AnalyticsService) { }


    private withAuth<T>(operationName: string, fn: (headers: HttpHeaders) => Observable<T>): Observable<T> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    const err = new Error('No token available');
                    this.handleError(operationName, err);
                    return throwError(() => err);
                }
                const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
                return fn(headers);
            }),
            catchError((err) => this.handleError(operationName, err))
        );
    }


    private handleError(operationName: string, error: unknown): Observable<never> {
        console.error(`AgendaService.${operationName} failed:`, error);
        return throwError(() => error instanceof Error ? error : new Error(String(error)));
    }


    private buildHttpParams(paramsObj: Record<string, string | number | boolean | undefined | null>): HttpParams {
        let params = new HttpParams();
        Object.entries(paramsObj).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        });
        return params;
    }

    /**
     * Obtiene la lista de agendas según filtros.
     */
    getAll(
        centerId?: number | null,
        employeeId?: number | null,
        startDate?: Date | null,
        endDate?: Date | null
    ): Observable<AgendaListResponse> {
        const params = this.buildHttpParams({
            centerId: centerId ?? undefined,
            employeeId: employeeId ?? undefined,
            startDate: startDate ? startDate.toISOString() : undefined,
            endDate: endDate ? endDate.toISOString() : undefined
        });

        return this.withAuth<AgendaListResponse>('getAll', (headers) =>
            this.http.get<AgendaListResponse>(this.apiUrl, { headers, params })
        );
    }

    /**
     * Obtener detalle de una agenda por id.
     */
    getById(id: number): Observable<AgendaDetail> {
        if (id == null) {
            return throwError(() => new Error('getById: id is required'));
        }
        const url = `${this.apiUrl}/${id}`;
        return this.withAuth<AgendaDetail>('getById', (headers) => this.http.get<AgendaDetail>(url, { headers }));
    }


    create(agenda: CreateAgendaDto): Observable<CreateAgendaResponse> {
        return this.withAuth<CreateAgendaResponse>('create', (headers) => {
            return this.http.post<CreateAgendaResponse>(this.apiUrl, agenda, { headers });
        }).pipe(
            tap((resp: CreateAgendaResponse) => {
                const agendaResp: AgendaPreview = 'agenda' in resp ? resp.agenda : resp;

                this.analytics.track(AnalyticsEvent.AgendaAddServiceSuccess, {
                    ...this.analytics.globalProps(),
                    service_id: agenda.serviceTypeId,
                    service_type: agendaResp.serviceName ?? '',
                    duration: agendaResp.duration ?? '',
                    price: agendaResp.service?.price ?? 0,
                });
            }),
            catchError((err: HttpErrorResponse) => {
                const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
                const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

                this.analytics.track(AnalyticsEvent.AgendaAddServiceFail, {
                    ...this.analytics.globalProps(),
                    service_type: agenda.serviceTypeId,
                    error_code: errorCode,
                    error_message: errorMessage,
                    http_status: err.status
                });

                return throwError(() => err);
            })
        );
    }

    /**
     * Actualizar agenda existente.
     */
    update(id: number, agenda: UpdateAgendaDto): Observable<Agenda> {
        if (id == null) {
            return throwError(() => new Error('update: id is required'));
        }
        const url = `${this.apiUrl}/${id}`;
        return this.withAuth<Agenda>('update', (headers) => this.http.put<Agenda>(url, agenda, { headers }));
    }

    /**
     * Mueve una agenda a otro centro en una nueva fecha/hora.
     * Endpoint: POST /api/Agenda/move-center
     */
    moveCenter(dto: MoveAgendaCenterRequestDto): Observable<Agenda> {
        if (!dto || dto.agendaId == null) {
            return throwError(() => new Error('moveCenter: dto.agendaId is required'));
        }
        const url = `${this.apiUrl}/move-center`;
        return this.withAuth<Agenda>('moveCenter', (headers) =>
            this.http.patch<Agenda>(url, dto, { headers })
        );
    }

    /**
     * Eliminar una agenda por id.
     */
    delete(id: number): Observable<void> {
        if (id == null) {
            return throwError(() => new Error('delete: id is required'));
        }
        const url = `${this.apiUrl}/${id}`;
        return this.withAuth<void>('delete', (headers) => this.http.delete<void>(url, { headers }));
    }

    /**
     * Confirmar agenda (endpoint confirm-agenda).
     */
    confirm(dto: ConfirmAgendaDto): Observable<any> {
        if (!dto || dto.agendaId == null) {
            return throwError(() => new Error('confirm: dto.agendaId is required'));
        }
        const url = `${this.apiUrl}/confirm-agenda`;
        return this.withAuth<any>('confirm', (headers) => this.http.post<any>(url, dto, { headers }));
    }

    /**
     * Revertir el estado de una agenda.
     */
    revert(id: number, targetStatus: AgendaStatus): Observable<Agenda> {
        if (id == null) {
            return throwError(() => new Error('revert: id is required'));
        }
        const url = `${this.apiUrl}/${id}/revert`;
        return this.withAuth<Agenda>('revert', (headers) =>
            this.http.put<Agenda>(url, { targetStatus }, { headers })
        );
    }

    /**
     * Comprobar conflictos para un empleado en un intervalo de tiempo.
     */
    checkConflict(
        employeeId: number,
        date: string,
        startTime: string,
        endTime: string,
        excludeEventId?: number
    ): Observable<boolean> {
        if (!employeeId || !date || !startTime || !endTime) {
            return throwError(() => new Error('checkConflict: employeeId, date, startTime and endTime are required'));
        }

        const params = this.buildHttpParams({
            employeeId: employeeId.toString(),
            date,
            startTime,
            endTime,
            excludeEventId: excludeEventId ?? undefined
        });

        const url = `${this.apiUrl}/conflict`;
        return this.withAuth<boolean>('checkConflict', (headers) =>
            this.http.get<boolean>(url, { headers, params })
        );
    }

    /**
     * Smart Assign: Get technician recommendation for a service.
     */
    smartAssign(request: SmartAssignRequest, locale: string = 'es'): Observable<SmartAssignResponse> {
        if (!request.centerId || !request.serviceTypeId || !request.agendaDateTimeUtc) {
            return throwError(() => new Error('smartAssign: centerId, serviceTypeId and agendaDateTimeUtc are required'));
        }

        const params = this.buildHttpParams({ locale });
        const url = `${this.apiUrl}/smart-assign`;

        return this.withAuth<SmartAssignResponse>('smartAssign', (headers) =>
            this.http.post<SmartAssignResponse>(url, request, { headers, params })
        );
    }

    smartAssignBulk(request: SmartAssignBulkRequest, locale: string = 'es'): Observable<SmartAssignBulkResponse> {
        if (!request.centerId || !request.agendaIds?.length) {
            return throwError(() => new Error('smartAssignBulk: centerId and agendaIds are required'));
        }

        const params = this.buildHttpParams({ locale });
        const url = `${this.apiUrl}/smart-assign/bulk`;

        return this.withAuth<SmartAssignBulkResponse>('smartAssignBulk', (headers) =>
            this.http.post<SmartAssignBulkResponse>(url, request, { headers, params })
        );
    }
}
